import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IProdutoFornecedor } from '@app/modules/fornecedor/models/fornecedor.model';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  ConfigService,
  EntityParams,
  EntityService,
  TokenService
} from 'viggo-sistemas-core-web';
import { IEntityPagination } from 'viggo-sistemas-core-web/modules/core/model/entity.model';
import { IGradeIncosistencia, IProdutoVariacao } from '../../model';
import { IProduto, IProdutoImagem } from '../../model/produto.model';

@Injectable()
export class ProdutoService extends EntityService<IProduto> {
  constructor(
    protected configService: ConfigService,
    protected tokenService: TokenService
  ) {
    super({
      baseEntityEndPoint: 'produto',
      urlBackend: configService.backendURL
    });
  }

  produtosFornecedor(
    params: string | EntityParams<IProdutoFornecedor>,
    domainId?: string,
    require_pagination = false
  ): Observable<any> {
    // T[] | IEntityPagination<T[]>
    let paramsAux: string = null;
    let paramsHttp = new HttpParams();
    if (typeof params === 'string') {
      paramsAux = `?${params}${
        require_pagination ? '&require_pagination=true' : ''
      }`;
      if (domainId) paramsAux += `&domain_id=${domainId}`;
    } else {
      // if (params.where) paramsHttp = this.getParams(params.where);
      if (domainId) paramsHttp = paramsHttp.set('domain_id', domainId);
      if (require_pagination) {
        paramsHttp = paramsHttp.set('require_pagination', require_pagination);
      }
      if (params.pagination) {
        paramsHttp = paramsHttp.set('page', params.pagination.page);
        paramsHttp = paramsHttp.set('page_size', params.pagination.page_size);
      }
      if (params.include && params.include.length)
        paramsHttp = paramsHttp.set('include', params.include.join(','));
      if (params.order_by && params.order_by.length)
        paramsHttp = paramsHttp.set('order_by', params.order_by.join(','));
      if (params.list_options)
        paramsHttp = paramsHttp.set('list_options', params.list_options);
      paramsAux = null;
    }

    const url = this.baseURL + '/fornecedores' + (paramsAux || '');

    return this.http
      .get<any>(url, {
        headers: this.getHeader(),
        params: paramsHttp
      })
      .pipe(
        map(data => {
          if (require_pagination) {
            data = data as IEntityPagination<IProdutoFornecedor>;
            return {
              data: data['produto_fornecedores'],
              pagination: data.pagination,
              valor_saida: data.valor_saida,
              valor_entrada: data.valor_entrada
            };
          }
          data = data as IProdutoFornecedor[];
          return data['produto_fornecedores'];
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Vincula um fornecedor ao produto
   * @param codigo_produto
   * @param fornecedor_id
   * @returns
   */
  registrarFornecedorNoProduto(
    produto_id: string,
    codigo_produto: string,
    fornecedor_id: string
  ): Observable<any> {
    const url = `${this.baseURL}/${produto_id}/registrar_fornecedor`;
    const headers = this.getHeader();
    return this.http.put<any>(
      url,
      {
        codigo_produto_fornecedor: codigo_produto,
        fornecedor_id: fornecedor_id
      },
      { headers }
    );
  }

  /**
   * Adciona uma imagem ao produto
   * @param produtoId identificador do produto
   * @param imagem
   */
  public addImagem(
    produtoId: string,
    imagem: File
  ): Observable<IProdutoImagem> {
    const url = `${this.baseURL}/${produtoId}/imagens`;
    const formData = new FormData();
    formData.append('file', imagem, imagem.name.toLowerCase());
    const headers = this.getHeader().delete('Content-Type');

    return this.http
      .post<{ produto_imagem: IProdutoImagem }>(url, formData, {
        headers
      })
      .pipe(map(response => response.produto_imagem));
  }

  /**
   * Remove a imagem de um produto
   * @param produtoId identificador do produto
   * @param imagemId identificador da imagem
   */
  public removerImagem(produtoId: string, imagemId: string) {
    const url = `${this.baseURL}/${produtoId}/imagens/${imagemId}`;

    return this.http.delete(url, { headers: this.getHeader() });
  }

  /**
   * Torna a imagem repassada favorita
   * @param produtoId identificador do produto
   * @param imagemId identificador da imagem
   */
  public favoritarImagem(produtoId: string, imagemId: string) {
    const url = `${this.baseURL}/${produtoId}/imagens/${imagemId}/favoritar`;
    return this.http.put(url, null, { headers: this.getHeader() });
  }

  /**
   * Responsável por resgatar os usuários disponíveis
   * @param cliente_id
   * @param domain_id
   * @param include
   * @param pagination
   * @param order_by
   * @param where
   * @returns
   */
  public listarProdutosComFiscal(
    domain_id: string,
    include?: string[],
    pagination?: {
      require_pagination?: boolean;
      page_size?: number;
      page?: number;
    },
    order_by?: string,
    where?: { field: string; value: string | number },
    variacoes = false
  ): Observable<any> {
    let params = new HttpParams().append('ligacao_com_fiscal', true);

    if (domain_id) {
      params = params.append('domain_id', domain_id);
    }

    if (include) {
      params = params.append('include', include.join(','));
    }

    if (pagination) {
      params = params
        .append('require_pagination', pagination.require_pagination)
        .append('page_size', pagination.page_size)
        .append('page', pagination.page);
    }

    if (where) {
      params = params.append(`${where.field}`, where.value);
    }

    if (order_by) {
      params = params.append('order_by', order_by);
    }

    return this.http.get<any>(
      variacoes ? this.baseURL + '/variacoes' : this.baseURL,
      {
        headers: this.getHeader(),
        params
      }
    );
  }

  /**
   * Pesquisa uma lista de entidades apartir de um parametro repassado
   * @param params parametros repassados
   * @param domainId identificador do domain a ser buscado
   */
  override getEntitiesByParams(
    params: string | EntityParams<IProduto>,
    domainId?: string,
    require_pagination = false,
    variacoes = false
  ): Observable<any> {
    // T[] | IEntityPagination<T[]>
    let paramsAux: string = null;
    let paramsHttp = new HttpParams();

    if (typeof params === 'string') {
      paramsAux = `?${params}${
        require_pagination ? '&require_pagination=true' : ''
      }`;
      if (domainId) paramsAux += `&domain_id=${domainId}`;
    } else {
      if (params.where) paramsHttp = this.getParams(params.where);
      if (domainId) paramsHttp = paramsHttp.set('domain_id', domainId);
      if (require_pagination) {
        paramsHttp = paramsHttp.set('require_pagination', require_pagination);
      }
      if (params.pagination) {
        paramsHttp = paramsHttp.set('page', params.pagination.page);
        paramsHttp = paramsHttp.set('page_size', params.pagination.page_size);
      }
      if (params.include && params.include.length)
        paramsHttp = paramsHttp.set('include', params.include.join(','));
      if (params.order_by && params.order_by.length)
        paramsHttp = paramsHttp.set('order_by', params.order_by.join(','));
      if (params.list_options)
        paramsHttp = paramsHttp.set('list_options', params.list_options);
      paramsAux = null;
    }

    const url = variacoes
      ? this.baseURL + '/variacoes' + (paramsAux || '')
      : this.baseURL + (paramsAux || '');

    return this.http
      .get<any>(url, {
        headers: this.getHeader(),
        params: paramsHttp
      })
      .pipe(
        map(data => {
          if (require_pagination) {
            data = data as IEntityPagination<IProduto>;
            return {
              data: data[this.entitiesName],
              pagination: data.pagination
            };
          }
          data = data as IProduto[];
          return data[this.entitiesName];
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Busca uma entidade pelo ID
   * @param id identificador da entidade
   * @param include relacionamentos a serem carregados, use apenas o array de strings
   */
  override getEntity(
    id: string,
    include?: string | string[],
    produto_variacao_id?: string
  ): Observable<IProduto> {
    const url = `${this.baseURL}/${id}`;
    let params = new HttpParams();
    if (include)
      params = params.set(
        'include',
        Array.isArray(include) ? include.join(',') : include
      );

    if (produto_variacao_id)
      params = params.append('produto_variacao_id', produto_variacao_id);

    return this.http
      .get<IProduto>(url, { headers: this.getHeader(), params })
      .pipe(
        map(data => data[this.entityName]),
        catchError(this.handleError)
      );
  }
  /**
   * Envia para a API um JSON das variações selecionadas e a descrição do produto
   * para obter de retorno os produtos variação
   * @param variacoes
   * @param descricao
   * @param produto_id
   */
  public gerarProdutosVariacoes(
    variacoes,
    descricao: string,
    produto_id?: string,
    valor_minimo = 0
  ): Observable<{
    produto_id: string;
    variacoes: IProdutoVariacao[];
  }> {
    const url = `${this.baseURL}/analisar_variacoes`;
    return this.http.post<{
      produto_id: string;
      variacoes: IProdutoVariacao[];
    }>(
      url,
      {
        variacoes: variacoes,
        produto_descricao: descricao,
        produto_id: produto_id,
        produto_valor_minimo: valor_minimo
      },
      { headers: this.getHeader() }
    );
  }

  /**
   * Zerar estoque do produto
   * @param produto_id
   * @returns
   */
  zerarEstoqueProduto(produto_id: string): Observable<any> {
    const url = `${this.baseURL}/${produto_id}/zerar_estoque`;
    const headers = this.getHeader();
    return this.http.put<any>(url, null, { headers });
  }

  /**
   * Dado um determinado produto é analisado onde está sendo utilizado
   * e caso o usuário queira solucionar essas inconsistências
   * deve encerrar o ciclo do produto nessas entidades para aplicar
   * a crição de grade
   * @param produto_id
   * @returns
   */
  analisarCriacaoGrade(produto_id: string): Observable<IGradeIncosistencia> {
    const url = `${this.baseURL}/${produto_id}/analisar_inconsistencia_grade`;
    const headers = this.getHeader();
    return this.http.get<IGradeIncosistencia>(url, { headers });
  }
}
